/**
 * 自機位置の送信。
 *
 * 受信は GV Bridge の SSE（BridgeConnection）が担うが、SSE は一方向なので
 * 送信には使えない。既存プロトコル互換の `POST /cgi-bin/sr.cgi` を使う
 * （vite.config.ts / 本番の Caddy 設定で同一オリジンにプロキシしてあるので、
 * 追加の CORS 対応は要らない）。
 *
 * 原典 GoalView は受信した GPS 入力を 2500ms ごとに間引いて送信する
 * （docs/01 §3.1）。ここでも同じ間隔を既定にする。
 */

import { RelayClient, buildGga, buildLine, buildRmc } from '@gv4d/core';

export interface TransmitFix {
  /** UTC epoch ミリ秒 */
  t: number;
  lat: number;
  lon: number;
  altMsl?: number | null;
  /** 対地速度 [m/s] */
  speed?: number | null;
  /** 進行方向 [deg] */
  heading?: number | null;
}

export type TransmitStatus = 'idle' | 'sending' | 'error';

const DEFAULT_THROTTLE_MS = 2500;

export class SelfPositionTransmitter {
  #client: RelayClient;
  #terminalId: string;
  #throttleMs: number;
  #startedAt = Date.now();
  #lastSentAt = 0;
  #sentCount = 0;

  status: TransmitStatus = 'idle';
  onStatus: ((status: TransmitStatus, message: string) => void) | null = null;

  constructor(options: { endpoint: string; terminalId: string; throttleMs?: number }) {
    this.#terminalId = options.terminalId;
    this.#throttleMs = options.throttleMs ?? DEFAULT_THROTTLE_MS;
    // 送信専用に使う。受信（onData）は BridgeConnection の SSE 側に任せ、
    // ここでは処理しない（二重取り込みを避けるため、意図的に何もしない）
    this.#client = new RelayClient({ serverUrl: options.endpoint, pollingMs: this.#throttleMs });
    this.#client.onError = (e) => {
      this.status = 'error';
      this.onStatus?.('error', (e as Error).message);
    };
    // ★ status を 'sending' にするのは、実際に HTTP 200 でポーリングが
    //   成功したときだけにする。update() でキューに積んだだけでは
    //   通信が成功した証拠にならない（PR#98 再レビュー指摘）。
    this.#client.onPollSuccess = () => {
      if (this.status !== 'sending') {
        this.status = 'sending';
        this.onStatus?.('sending', '送信中');
      }
    };
  }

  get sentCount(): number {
    return this.#sentCount;
  }

  get pendingCount(): number {
    return this.#client.pendingCount;
  }

  /** 最後に実際に送信した時刻（UTC epoch ミリ秒）。まだ一度も送っていなければ0 */
  get lastSentAt(): number {
    return this.#lastSentAt;
  }

  /** 送信の間引き間隔 [ms]。「どれだけ間隔が空いたら止まっていたとみなすか」の目安に使う */
  get throttleMs(): number {
    return this.#throttleMs;
  }

  /**
   * 新しい位置が得られるたびに呼ぶ。間引きはここで行うので、GPS の
   * `watchPosition` が高頻度で呼んでも安全（呼び出し側は気にしなくてよい）。
   */
  update(fix: TransmitFix): void {
    const now = Date.now();
    if (now - this.#lastSentAt < this.#throttleMs) return;
    this.#lastSentAt = now;

    const elapsedMs = now - this.#startedAt;
    const gga = buildGga({ t: fix.t, lat: fix.lat, lon: fix.lon, altMsl: fix.altMsl });
    const rmc = buildRmc({
      t: fix.t,
      lat: fix.lat,
      lon: fix.lon,
      speed: fix.speed,
      heading: fix.heading,
    });
    this.#client.send(buildLine(this.#terminalId, elapsedMs, gga));
    this.#client.send(buildLine(this.#terminalId, elapsedMs, rmc));
    this.#sentCount++;
  }

  start(): void {
    this.#client.start();
  }

  stop(): void {
    this.#client.stop();
    this.status = 'idle';
  }
}

/**
 * 送信が実際にどれくらい止まっていたかを判定し、知らせるメッセージを返す。
 * 画面が背景に回っている間はGPS取得・送信自体が止まりうる（PWAの制約、
 * docs/08参照）ので、前面に戻ってきたタイミングでこれを呼ぶ想定。
 *
 * 間引き待ちの通常の谷を誤検知しないよう、間引き間隔の3倍以上
 * （最低60秒）あいたときだけ知らせる。知らせる必要が無ければnull。
 */
export function staleSinceMessage(nowMs: number, lastSentAtMs: number, throttleMs: number): string | null {
  if (lastSentAtMs === 0) return null;
  const idleMs = nowMs - lastSentAtMs;
  const threshold = Math.max(60_000, throttleMs * 3);
  if (idleMs <= threshold) return null;
  // 閾値に60秒の下駄があるため、ここに来る時点でidleMsは必ず60秒を超えている
  // （= 分単位で表示できる。0分になることはない）
  const minutes = Math.round(idleMs / 60_000);
  return `送信が約${minutes}分止まっていました（バックグラウンドで停止した可能性があります）`;
}
